// Per-user "/me/*" endpoints (Multi-user Stage B.1 / B.4 / B.5).
// All routes are owner-scoped server-side via Auth0 JWT → user_id.

import { apiGet, apiPatch } from "./client";

// ----- /me/activity ---------------------------------------------------------

export interface ActivityTraceRow {
  trace_id: string;
  session_id: string | null;
  created_at: string; // ISO-8601
  query_preview: string;
  answer_summary_preview: string;
  route_class: string | null;
  grounding_score: number | null;
  refine_used: boolean;
  cost_usd: string | null; // Decimal serialised
  tool_count: number;
  sub_agents_used: string[];
  status: string | null;
  latency_ms: number | null;
}

export interface ActivityResponse {
  traces: ActivityTraceRow[];
  next_cursor: string | null;
}

export interface ActivityFilters {
  routeClasses?: string[];
  subAgents?: string[];
  since?: string | null; // ISO-8601
  until?: string | null;
  minGrounding?: number | null;
  maxGrounding?: number | null;
  refineOnly?: boolean;
  minCost?: string | null; // Decimal as string to preserve precision
  maxCost?: string | null;
  minLatencyMs?: number | null;
  maxLatencyMs?: number | null;
  errorOnly?: boolean;
}

function appendFilters(params: URLSearchParams, filters: ActivityFilters): void {
  if (filters.routeClasses?.length) params.set("route_class", filters.routeClasses.join(","));
  if (filters.subAgents?.length) params.set("sub_agent", filters.subAgents.join(","));
  if (filters.since) params.set("since", filters.since);
  if (filters.until) params.set("until", filters.until);
  if (filters.minGrounding != null) params.set("min_grounding", String(filters.minGrounding));
  if (filters.maxGrounding != null) params.set("max_grounding", String(filters.maxGrounding));
  if (filters.refineOnly) params.set("refine_only", "true");
  if (filters.minCost) params.set("min_cost", filters.minCost);
  if (filters.maxCost) params.set("max_cost", filters.maxCost);
  if (filters.minLatencyMs != null) params.set("min_latency_ms", String(filters.minLatencyMs));
  if (filters.maxLatencyMs != null) params.set("max_latency_ms", String(filters.maxLatencyMs));
  if (filters.errorOnly) params.set("error_only", "true");
}

export function fetchActivity(
  cursor?: string | null,
  filters: ActivityFilters = {},
  limit = 20,
): Promise<ActivityResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  appendFilters(params, filters);
  return apiGet<ActivityResponse>(`/me/activity?${params.toString()}`);
}

// ----- /me/activity/stats (Stage B.5+) -------------------------------------

export type ActivityWindow = "7d" | "30d" | "90d";

export interface ActivityDailyBucket {
  bucket: string; // YYYY-MM-DD
  turn_count: number;
  median_grounding: number | null;
  cost_usd: string;
  median_latency_ms: number | null;
  error_count: number;
}

export interface ActivitySubAgentRow {
  sub_agent: string;
  turn_count: number;
  cost_usd: string;
  median_grounding: number | null;
  median_latency_ms: number | null;
}

export interface ActivityRouteClassRow {
  route_class: string;
  turn_count: number;
  cost_usd: string;
}

export interface ActivityStatsResponse {
  window_days: number;
  daily: ActivityDailyBucket[];
  by_sub_agent: ActivitySubAgentRow[];
  by_route_class: ActivityRouteClassRow[];
  totals: { turn_count: number; cost_usd: string; error_count: number };
}

export function fetchActivityStats(window: ActivityWindow): Promise<ActivityStatsResponse> {
  return apiGet<ActivityStatsResponse>(`/me/activity/stats?window=${window}`);
}

// ----- /me/profile (Stage B.4) ---------------------------------------------

export interface UserProfile {
  user_id: string;
  welcomed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export function fetchProfile(): Promise<UserProfile> {
  return apiGet<UserProfile>("/me/profile");
}

export function markWelcomed(): Promise<UserProfile> {
  return apiPatch<UserProfile, { welcomed: true }>("/me/profile", { welcomed: true });
}

// ----- /me/spend (Stage B.1) ------------------------------------------------

export interface SpendByRouteRow {
  route_class: string;
  n: number;
  spend: string;
}

export interface SpendRecentTrace {
  trace_id: string;
  session_id: string | null;
  route_class: string;
  cost_usd: string;
  tool_calls: number;
  ended_at: string;
}

export interface MeSpendResponse {
  user_id: string;
  monthly_cap_usd: string;
  month_to_date_usd: string;
  remaining_usd: string;
  percent_of_cap: number;
  resets_at: string;
  warn: boolean;
  exceeded: boolean;
  by_route_class: SpendByRouteRow[];
  recent_traces: SpendRecentTrace[];
  trace_count_this_month: number;
}

export function fetchMeSpend(): Promise<MeSpendResponse> {
  return apiGet<MeSpendResponse>("/me/spend");
}
